import { GET, POST, PUT, DELETE } from '../../../services/api'
import {
  SellerQueueItem,
  SellerServiceSessionItem,
  SellerServiceNoSaleReasonItem,
  NoSaleReasonCodeOption,
  NoSaleReasonStat,
  SellerConfigItem,
  ConferenceOrderFilterPayload,
  ConferenceOrderItem,
  ConferenceOrderSource,
  ConferenceSeller,
  ConferenceTotvsResult,
} from './types'
import {
  buildNinegridConferenceRequests,
  buildTotvsConferenceRequests,
} from './conference-utils'

const normalizeApiData = <T>(response: any): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    return response.data as T
  }

  return response as T
}

const withConferenceOrderSource = (
  orders: ConferenceOrderItem[],
  source: ConferenceOrderSource
): ConferenceOrderItem[] => orders.map((order) => ({ ...order, source }))

const toRecord = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }

  return value as Record<string, unknown>
}

const pickString = (record: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value)
    }
  }

  return ''
}

const pickNumber = (record: Record<string, unknown>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }

  return undefined
}

const pemToArrayBuffer = (pem: string): ArrayBuffer => {
  const sanitized = pem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s+/g, '')

  const binary = window.atob(sanitized)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  return bytes.buffer
}

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index])
  }
  return window.btoa(binary)
}

const encryptAuthorizationCredential = async (
  credential: string,
  publicKeyPem: string
): Promise<string> => {
  if (!window.crypto?.subtle) {
    throw new Error('Criptografia não suportada neste navegador')
  }

  const importedKey = await window.crypto.subtle.importKey(
    'spki',
    pemToArrayBuffer(publicKeyPem),
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  )

  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    importedKey,
    new TextEncoder().encode(credential)
  )

  return arrayBufferToBase64(encrypted)
}

const extractTotvsArray = (response: any): unknown[] => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.Dados?.result)) return response.Dados.result
  if (Array.isArray(response?.Dados?.result?.items)) return response.Dados.result.items
  if (Array.isArray(response?.Dados?.result?.data)) return response.Dados.result.data
  if (Array.isArray(response?.Dados?.result?.orders)) return response.Dados.result.orders
  if (Array.isArray(response?.Dados)) return response.Dados
  if (Array.isArray(response?.Dados?.items)) return response.Dados.items
  if (Array.isArray(response?.Dados?.data)) return response.Dados.data
  if (Array.isArray(response?.Dados?.orders)) return response.Dados.orders
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.data?.items)) return response.data.items
  if (Array.isArray(response?.items)) return response.items
  if (Array.isArray(response?.orders)) return response.orders
  return []
}

const mapTotvsOrderToConferenceItem = (item: unknown, index: number): ConferenceOrderItem => {
  const record = toRecord(item)
  const customer = toRecord(record.customer)
  const counts = toRecord(record.counts)
  const shippingAddress = toRecord(record.shippingAddress)
  const commissioneds = Array.isArray(record.commissioneds) ? record.commissioneds : []
  const firstCommissioned = toRecord(commissioneds[0])

  const orderCode = pickString(record, ['orderCode', 'id'])
  const customerOrderCode = pickString(record, ['customerOrderCode', 'orderId'])
  const customerName =
    pickString(customer, ['name', 'fullName']) ||
    pickString(record, ['customerName']) ||
    pickString(shippingAddress, ['name'])
  const sellerCpf =
    pickString(firstCommissioned, ['sellerCpfCnpj', 'representativeCpfCnpj', 'cpfCnpj']) ||
    pickString(record, ['sellerCpfCnpj', 'representativeCpfCnpj'])
  const sellerCode =
    pickString(firstCommissioned, ['sellerCode', 'representativeCode', 'code']) ||
    pickString(record, ['sellerCode', 'representativeCode'])
  const totalValue =
    pickNumber(counts, ['grossValue', 'netValue', 'totalValue']) ??
    pickNumber(record, ['TotalAmountOrder', 'totalAmountOrder']) ??
    0

  const statusLabel =
    pickString(record, ['statusOrderName', 'statusDescription', 'status']) ||
    pickString(firstCommissioned, ['status']) ||
    (record.statusOrder != null ? String(record.statusOrder) : '-')

  return {
    id: orderCode || customerOrderCode || `totvs-${index}`,
    orderidExt: customerOrderCode || orderCode || null,
    created:
      pickString(record, ['maxChangeFilterDate', 'orderDate', 'created']) || new Date().toISOString(),
    customer: {
      name: customerName || '-',
    },
    content: {
      sellerCpf,
      sellerCode,
      rawTotvsOrder: record,
    },
    total: Math.round(totalValue * 100),
    statusLabel,
    source: 'totvs',
  }
}

// ============ SELLER QUEUE API ============

export const getQueueByPluginspace = async (
  pluginspaceId?: number
): Promise<SellerQueueItem[]> => {
  try {
    if (pluginspaceId !== undefined && pluginspaceId !== null && pluginspaceId <= 0) {
      return []
    }

    console.log('[services] getQueueByPluginspace - Fetching from /seller-queue/pluginspace')
    const query = pluginspaceId ? `?pluginspaceId=${pluginspaceId}` : ''
    const response = await GET(`/seller-queue/pluginspace${query}`)
    const data = normalizeApiData<SellerQueueItem[]>(response) || []
    console.log('[services] getQueueByPluginspace - Got response with', data.length, 'items')
    if (data.length > 0) {
      console.log('[services] getQueueByPluginspace - First item full structure:', JSON.stringify(data[0], null, 2))
    }
    return data
  } catch (error) {
    console.error('[services] getQueueByPluginspace - ERROR:', error)
    throw error
  }
}

export const getQueueByStatus = async (
  status: string
): Promise<SellerQueueItem[]> => {
  try {
    const response = await GET(`/seller-queue/status/${status}`)
    return normalizeApiData<SellerQueueItem[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const getNextAvailable = async (): Promise<SellerQueueItem | null> => {
  try {
    const response = await GET(`/seller-queue/next-available`)
    return normalizeApiData<SellerQueueItem | null>(response) || null
  } catch (error) {
    throw error
  }
}

export const createQueueEntry = async (
  pluginspaceId: number,
  sellerId: number,
  position?: number
): Promise<SellerQueueItem> => {
  try {
    const response = await POST('/seller-queue/createQueueEntry', {
      pluginspaceId,
      sellerId,
      ...(position && position > 0 ? { position } : {}),
    })
    return normalizeApiData<SellerQueueItem>(response)
  } catch (error) {
    throw error
  }
}

export const updateQueueStatus = async (id: number, status: string): Promise<void> => {
  try {
    await PUT(`/seller-queue/${id}/status`, { status })
  } catch (error) {
    throw error
  }
}

export const updateQueuePosition = async (
  sellerId: number,
  newPosition: number,
  pluginspaceId: number
): Promise<void> => {
  try {
    await PUT('/seller-queue/updatePosition', { sellerId, newPosition, pluginspaceId })
  } catch (error) {
    throw error
  }
}

export const batchUpdateQueuePositions = async (
  updates: Array<{ sellerId: number; position: number }>,
  authCredential?: string
): Promise<void> => {
  try {
    const payload = { updates, ...(authCredential ? { authCredential } : {}) }
    console.log('[services] batchUpdateQueuePositions - START - Sending', updates.length, 'updates')
    // Axios aqui tem timeout de 15s, então não precisa de withTimeout adicional
    const response = await PUT('/seller-queue/batchUpdatePositions', payload)
    console.log('[services] batchUpdateQueuePositions - SUCCESS - Response:', response)
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error)
    console.error('[services] batchUpdateQueuePositions - ERROR:', errorMsg)
    throw new Error(`Falha ao atualizar fila: ${errorMsg}`)
  }
}

export const updateLastCalledAt = async (id: number): Promise<void> => {
  try {
    await PUT(`/seller-queue/${id}/last-called`, {})
  } catch (error) {
    throw error
  }
}

export const updateLastFinishedAt = async (id: number): Promise<void> => {
  try {
    await PUT(`/seller-queue/${id}/last-finished`, {})
  } catch (error) {
    throw error
  }
}

export const removeFromQueue = async (
  sellerId: number
): Promise<void> => {
  try {
    console.log(`[removeFromQueue] Starting removal of sellerId=${sellerId}`);
    const response = await DELETE(`/seller-queue/seller/${sellerId}`);
    console.log(`[removeFromQueue] Response:`, response);
    
    if (response.success === false) {
      throw new Error(response.error || response.message || 'Falha ao remover');
    }
    
    console.log(`[removeFromQueue] ✅ Successfully removed sellerId=${sellerId}`);
  } catch (error) {
    console.error(`[removeFromQueue] ❌ Error removing sellerId=${sellerId}:`, error);
    throw error;
  }
}

export const startSellerAttendance = async (
  sellerId: number,
  queuePositionAtStart?: number
): Promise<void> => {
  try {
    const normalizedQueuePosition = Number(queuePositionAtStart)
    const shouldSendQueuePosition =
      Number.isInteger(normalizedQueuePosition) && normalizedQueuePosition > 0

    const response = await POST('/seller-queue/start-attendance', {
      sellerId,
      ...(shouldSendQueuePosition ? { queuePositionAtStart: normalizedQueuePosition } : {}),
    })
    if (response?.success === false) {
      throw new Error(response?.message || 'Falha ao iniciar atendimento')
    }
  } catch (error) {
    throw error
  }
}

export const closeSellerAttendance = async (
  sessionId: number,
  sellerId: number,
  outcome: 'sale_completed' | 'pending_checkout' | 'no_sale' | 'abandoned',
  reasonCode?: number,
  note?: string
): Promise<void> => {
  try {
    const response = await POST('/seller-queue/close-attendance', {
      sessionId,
      sellerId,
      outcome,
      reasonCode,
      note,
    })
    if (response?.success === false) {
      throw new Error(response?.message || 'Falha ao encerrar atendimento')
    }
  } catch (error) {
    throw error
  }
}

// ============ SELLER SERVICE SESSION API ============

export const getSessionHistory = async (
  pluginspaceId: number,
  startDate?: string,
  endDate?: string
): Promise<SellerServiceSessionItem[]> => {
  try {
    let url = `/seller-service-session/pluginspace/${pluginspaceId}`
    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`
    }
    const response = await GET(url)
    const rawSessions = normalizeApiData<SellerServiceSessionItem[]>(response) || []

    return (rawSessions || []).map((session: any) => ({
      ...session,
      sellerName:
        session?.sellerName ||
        session?.sellerQueue?.user?.name ||
        session?.sellerQueue?.user?.username ||
        undefined,
    }))
  } catch (error) {
    throw error
  }
}

export const getSessionById = async (sessionId: number): Promise<SellerServiceSessionItem> => {
  try {
    const response = await GET(`/seller-service-session/${sessionId}`)
    return normalizeApiData<SellerServiceSessionItem>(response)
  } catch (error) {
    throw error
  }
}

export const updateSessionStatus = async (sessionId: number, status: string): Promise<void> => {
  try {
    await PUT(`/seller-service-session/${sessionId}`, { status })
  } catch (error) {
    throw error
  }
}

export const completeSession = async (
  sessionId: number,
  outcome: string,
  durationSeconds: number
): Promise<void> => {
  try {
    await PUT(`/seller-service-session/${sessionId}`, { outcome, durationSeconds })
  } catch (error) {
    throw error
  }
}

// ============ NO SALE REASONS API ============

export const getNoSaleReasonsBySession = async (
  sessionId: number
): Promise<SellerServiceNoSaleReasonItem[]> => {
  try {
    const response = await GET(`/seller-service-no-sale-reason/session/${sessionId}`)
    return normalizeApiData<SellerServiceNoSaleReasonItem[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const createNoSaleReason = async (
  sessionId: number,
  reasonCode: number,
  productName?: string,
  sizeWanted?: string,
  colorWanted?: string,
  note?: string
): Promise<SellerServiceNoSaleReasonItem> => {
  try {
    const response = await POST('/seller-service-no-sale-reason', {
      sessionId,
      reasonCode,
      productName,
      sizeWanted,
      colorWanted,
      note,
    })
    return normalizeApiData<SellerServiceNoSaleReasonItem>(response)
  } catch (error) {
    throw error
  }
}

export const getNoSaleReasonCodes = async (): Promise<NoSaleReasonCodeOption[]> => {
  try {
    const response = await GET('/seller-service-no-sale-reason/reason-codes')
    return normalizeApiData<NoSaleReasonCodeOption[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const getNoSaleReasonStatistics = async (
  pluginspaceId: number,
  days?: number,
  startDate?: string,
  endDate?: string,
  sellerId?: number,
): Promise<NoSaleReasonStat[]> => {
  try {
    let url = `/seller-service-no-sale-reason/statistics/${pluginspaceId}`
    if (days) {
      url += `?days=${days}`
    }

    if (startDate) {
      url += `${url.includes('?') ? '&' : '?'}startDate=${startDate}`
    }

    if (endDate) {
      url += `${url.includes('?') ? '&' : '?'}endDate=${endDate}`
    }

    if (sellerId) {
      url += `${url.includes('?') ? '&' : '?'}sellerId=${sellerId}`
    }

    const response = await GET(url)
    return normalizeApiData<NoSaleReasonStat[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const getSessionStatistics = async (
  pluginspaceId: number,
  sellerId?: number
): Promise<any> => {
  try {
    let url = `/seller-service-session/statistics?pluginspaceId=${pluginspaceId}`
    if (sellerId) {
      url += `&sellerId=${sellerId}`
    }
    const response = await GET(url)
    return normalizeApiData<any>(response)
  } catch (error) {
    throw error
  }
}

// ============ USERS API ============

export const getUsersByType = async (type: string = 'seller'): Promise<any[]> => {
  try {
    const response = await POST(`/users/filter`, { type: 'seller', status: true })
    return normalizeApiData<any[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const getUsersByTypeRoute = async (userType: string = 'seller'): Promise<ConferenceSeller[]> => {
  try {
    const response = await GET(`/users/type/${userType}`)
    const data = normalizeApiData<ConferenceSeller[]>(response) || []

    return data.filter((user) => user.type === userType && user.status !== false)
  } catch (error) {
    throw error
  }
}

export const filterAdminQueueSellerOrders = async (
  payload: ConferenceOrderFilterPayload,
  page: number = 1,
  take: number = 5000,
  order: 'ASC' | 'DESC' = 'DESC'
): Promise<ConferenceOrderItem[]> => {
  const parseNinegridResponse = (response: any): ConferenceOrderItem[] => {
    if (Array.isArray(response)) {
      return withConferenceOrderSource(response as ConferenceOrderItem[], 'ninegrid')
    }

    if (response && typeof response === 'object') {
      const directData = (response as { data?: unknown }).data
      if (Array.isArray(directData)) {
        return withConferenceOrderSource(directData as ConferenceOrderItem[], 'ninegrid')
      }

      if (
        directData &&
        typeof directData === 'object' &&
        Array.isArray((directData as { data?: unknown }).data)
      ) {
        return withConferenceOrderSource(
          (directData as { data: ConferenceOrderItem[] }).data,
          'ninegrid'
        )
      }
    }

    return []
  }

  const fetchNinegridOrders = async (
    requestPayload: Omit<ConferenceOrderFilterPayload, 'orderSearch'>
  ): Promise<ConferenceOrderItem[]> => {
    const response = await POST(
      `/orders/filter-admin-queue-seller?page=${page}&take=${take}&order=${order}`,
      requestPayload
    )
    return parseNinegridResponse(response)
  }

  try {
    const requestPayloads = buildNinegridConferenceRequests(payload)

    const mergedOrders = new Map<string, ConferenceOrderItem>()
    for (const requestPayload of requestPayloads) {
      const orders = await fetchNinegridOrders(requestPayload)
      for (const orderItem of orders) {
        const dedupeKey = `${String(orderItem.id)}::${orderItem.orderidExt ?? ''}`
        if (!mergedOrders.has(dedupeKey)) {
          mergedOrders.set(dedupeKey, orderItem)
        }
      }
    }

    return Array.from(mergedOrders.values())
  } catch (error) {
    throw error
  }
}

export const filterAdminQueueSellerReportOrders = async (
  payload: ConferenceOrderFilterPayload,
  page: number = 1,
  take: number = 5000,
  order: 'ASC' | 'DESC' = 'DESC'
): Promise<ConferenceOrderItem[]> => {
  const parseNinegridResponse = (response: any): ConferenceOrderItem[] => {
    if (Array.isArray(response)) {
      return withConferenceOrderSource(response as ConferenceOrderItem[], 'ninegrid')
    }

    if (response && typeof response === 'object') {
      const directData = (response as { data?: unknown }).data
      if (Array.isArray(directData)) {
        return withConferenceOrderSource(directData as ConferenceOrderItem[], 'ninegrid')
      }

      if (
        directData &&
        typeof directData === 'object' &&
        Array.isArray((directData as { data?: unknown }).data)
      ) {
        return withConferenceOrderSource(
          (directData as { data: ConferenceOrderItem[] }).data,
          'ninegrid'
        )
      }
    }

    return []
  }

  const fetchNinegridOrders = async (
    requestPayload: Omit<ConferenceOrderFilterPayload, 'orderSearch'>
  ): Promise<ConferenceOrderItem[]> => {
    const response = await POST(
      `/orders/filter-admin-queue-seller/report?page=${page}&take=${take}&order=${order}`,
      requestPayload
    )
    return parseNinegridResponse(response)
  }

  try {
    const requestPayloads = buildNinegridConferenceRequests(payload)

    const mergedOrders = new Map<string, ConferenceOrderItem>()
    for (const requestPayload of requestPayloads) {
      const orders = await fetchNinegridOrders(requestPayload)
      for (const orderItem of orders) {
        const dedupeKey = `${String(orderItem.id)}::${orderItem.orderidExt ?? ''}`
        if (!mergedOrders.has(dedupeKey)) {
          mergedOrders.set(dedupeKey, orderItem)
        }
      }
    }

    return Array.from(mergedOrders.values())
  } catch (error) {
    throw error
  }
}

export const filterTotvsConferenceOrders = async (
  payload: ConferenceOrderFilterPayload,
  page: number = 1,
  pageSize: number = 500
): Promise<ConferenceTotvsResult> => {
  try {
    const requests = buildTotvsConferenceRequests(payload, page, pageSize)
    const mergedItems = new Map<string, ConferenceOrderItem>()

    for (const request of requests) {
      const response = await POST('/order-fashion/sellerOrderDirectTotvs', request)
      const items = extractTotvsArray(response)
      const mappedItems = items.map((item, index) => mapTotvsOrderToConferenceItem(item, index))

      for (const mappedItem of mappedItems) {
        const dedupeKey = `${String(mappedItem.id)}::${mappedItem.orderidExt ?? ''}`
        if (!mergedItems.has(dedupeKey)) {
          mergedItems.set(dedupeKey, mappedItem)
        }
      }
    }

    const consolidatedItems = Array.from(mergedItems.values())
    return {
      items: consolidatedItems,
      meta: {
        page,
        pageSize,
        totalItems: consolidatedItems.length,
        totalPages: Math.max(1, Math.ceil(consolidatedItems.length / pageSize) || 1),
        hasNextPage: false,
      },
    }
  } catch (error) {
    throw error
  }
}

export const filterTotvsReportOrders = async (
  payload: ConferenceOrderFilterPayload,
  page: number = 1,
  pageSize: number = 500
): Promise<ConferenceTotvsResult> => {
  try {
    const requests = buildTotvsConferenceRequests(payload, page, pageSize)
    const mergedItems = new Map<string, ConferenceOrderItem>()

    for (const request of requests) {
      const response = await POST('/order-fashion/sellerOrderDirectTotvs/report', request)
      const items = extractTotvsArray(response)
      const mappedItems = items.map((item, index) => mapTotvsOrderToConferenceItem(item, index))

      for (const mappedItem of mappedItems) {
        const dedupeKey = `${String(mappedItem.id)}::${mappedItem.orderidExt ?? ''}`
        if (!mergedItems.has(dedupeKey)) {
          mergedItems.set(dedupeKey, mappedItem)
        }
      }
    }

    const consolidatedItems = Array.from(mergedItems.values())
    return {
      items: consolidatedItems,
      meta: {
        page,
        pageSize,
        totalItems: consolidatedItems.length,
        totalPages: Math.max(1, Math.ceil(consolidatedItems.length / pageSize) || 1),
        hasNextPage: false,
      },
    }
  } catch (error) {
    throw error
  }
}

export const authorizeSellerQueueAction = async (
  actionType:
    | 'manual_queue_move'
    | 'return_to_queue'
    | 'conference_search_all'
    | 'conference_search_seller',
  credential: string,
  targetSellerId?: number
): Promise<{ authorized: boolean; message?: string }> => {
  try {
    const normalizedCredential = credential.trim()
    if (!normalizedCredential) {
      return {
        authorized: false,
        message: 'Senha ou PIN é obrigatório',
      }
    }

    const challengeResponse = await POST('/seller-queue/authorize-challenge', {
      actionType,
      ...(targetSellerId ? { targetSellerId } : {}),
    })

    const challengeData = normalizeApiData<any>(challengeResponse) || challengeResponse
    const challengeId: string = challengeData?.challengeId
    const publicKey: string = challengeData?.publicKey

    if (!challengeId || !publicKey) {
      return {
        authorized: false,
        message: 'Falha ao obter challenge de autorização',
      }
    }

    const encryptedCredential = await encryptAuthorizationCredential(
      normalizedCredential,
      publicKey
    )

    const response = await POST('/seller-queue/authorize-action', {
      actionType,
      challengeId,
      encryptedCredential,
      ...(targetSellerId ? { targetSellerId } : {}),
    })
    return {
      authorized: !!response.authorized,
      message: response.message,
    }
  } catch (error) {
    return {
      authorized: false,
      message: error instanceof Error ? error.message : String(error),
    }
  }
}

export const hasTotvsIntegrationForConference = async (): Promise<boolean> => {
  try {
    const response = await POST('/pluginconfig/filter', {
      plugintypeId: 17,
      plugintemplateId: 23,
    })

    if (Array.isArray(response)) {
      return response.length > 0
    }

    if (response && typeof response === 'object') {
      const data = (response as { data?: unknown }).data

      if (Array.isArray(data)) {
        return data.length > 0
      }

      if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
        return ((data as { data: unknown[] }).data || []).length > 0
      }
    }

    return false
  } catch (error) {
    return false
  }
}

export const returnSellerToQueue = async (
  sessionId: number,
  sellerId: number,
  credential: string,
  outcome: 'sale_completed' | 'no_sale',
  reasonCode?: number,
  note?: string
): Promise<void> => {
  try {
    await POST('/seller-queue/return-to-queue', {
      sessionId,
      sellerId,
      credential,
      outcome,
      reasonCode,
      note,
    })
  } catch (error) {
    throw error
  }
}

// ============ SELLER CONFIG API ============

export const getSellerConfig = async (): Promise<SellerConfigItem | null> => {
  try {
    console.log('[services] getSellerConfig - Fetching from /seller-config/pluginspace')
    const response = await GET('/seller-config/pluginspace')
    console.log('[services] getSellerConfig - Full response:', response)
    
    // Aceita payload direto, {data: ...} ou {data: {data: ...}}
    const configData = response?.data?.data || response?.data || response
    console.log('[services] getSellerConfig - Extracted config:', configData)
    
    return configData || null
  } catch (error) {
    console.error('[services] getSellerConfig - ERROR:', error)
    throw error
  }
}

export const getSellerPanelLink = async (baseUrl: string): Promise<string> => {
  try {
    const response = await GET(
      `/seller-config/panel-link?baseUrl=${encodeURIComponent(baseUrl)}`
    )
    return response?.data?.link || response?.link || ''
  } catch (error) {
    throw error
  }
}

export const resolveSellerPanelToken = async (panelToken: string): Promise<number | null> => {
  try {
    const response = await GET(
      `/seller-config/panel-access/resolve?panelToken=${encodeURIComponent(panelToken)}`
    )
    const pluginspaceId = Number(response?.data?.pluginspaceId || response?.pluginspaceId || 0)
    return Number.isInteger(pluginspaceId) && pluginspaceId > 0 ? pluginspaceId : null
  } catch (error) {
    return null
  }
}

export const createSellerConfig = async (
  configData: SellerConfigItem
): Promise<SellerConfigItem> => {
  try {
    console.log('[services] createSellerConfig - POST /seller-config/create', configData)
    const response = await POST('/seller-config/create', configData)
    console.log('[services] createSellerConfig - Full response:', response)
    
    // Aceita payload direto, {data: ...} ou {data: {data: ...}}
    const result = response?.data?.data || response?.data || response
    console.log('[services] createSellerConfig - Extracted result:', result)
    
    return result
  } catch (error) {
    console.error('[services] createSellerConfig - ERROR:', error)
    throw error
  }
}

export const updateSellerConfig = async (
  configData: SellerConfigItem
): Promise<SellerConfigItem> => {
  try {
    console.log('[services] updateSellerConfig - PUT /seller-config/update', configData)
    const response = await PUT('/seller-config/update', configData)
    console.log('[services] updateSellerConfig - Full response:', response)
    
    // Aceita payload direto, {data: ...} ou {data: {data: ...}}
    const result = response?.data?.data || response?.data || response
    console.log('[services] updateSellerConfig - Extracted result:', result)
    
    return result
  } catch (error) {
    console.error('[services] updateSellerConfig - ERROR:', error)
    throw error
  }
}

export const deleteSellerConfig = async (): Promise<void> => {
  try {
    console.log('[services] deleteSellerConfig - DELETE /seller-config/delete')
    await DELETE('/seller-config/delete')
    console.log('[services] deleteSellerConfig - Success')
  } catch (error) {
    console.error('[services] deleteSellerConfig - ERROR:', error)
    throw error
  }
}
